import LogoWithText from '@/assets/images/view-metrics-logo-text.svg';
import { useAtomValue } from 'jotai';
import { Skeleton } from './ui/skeleton';
import { NavigationSkeleton } from './ui/sidebar/NavigationSkeleton';
import { sidebarAtom } from './ui/sidebar';

export const AppDashboardContentAreaSkeleton = () => (
  <div className="flex-1 flex flex-col min-w-0 bg-[#F8FAFC] relative z-10">
    {/* Top loader bar */}
    <div className="h-1 w-full bg-gray-100 overflow-hidden">
      <div className="h-full w-full relative">
        <div className="absolute h-full w-1/2 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 animate-[slide_1s_linear_infinite] translate-x-[-100%]" />
        <div className="absolute h-full w-1/2 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 animate-[slide_1s_linear_infinite] translate-x-[-100%] delay-500" />
      </div>
    </div>

    {/* Top nav */}
    <header className="h-[72px] px-4 md:px-8 flex items-center justify-between border-b border-grayscale-100 gap-4 bg-white">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-40 rounded" />
      </div>
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="hidden md:block">
          <Skeleton className="h-4 w-24 mb-1" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-8 w-8 rounded-full" />
      </div>
    </header>

    {/* Main dashboard content */}
    <main className="flex-1 w-full p-4 md:p-6 flex flex-col gap-6 overflow-y-auto">
      {/* Welcome header */}
      <div className="flex justify-between items-center">
        <Skeleton className="h-11 w-64 bg-white" />
        <Skeleton className="h-11 w-32 rounded-md bg-white" />
      </div>

      {/* Stats cards row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl p-6">
            <Skeleton className="h-4 w-1/2 mb-2" />
            <Skeleton className="h-8 w-3/4 mb-2" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>

      {/* Chart and metrics */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Chart card */}
        <div className="flex-1 bg-white rounded-xl p-6 flex flex-col gap-4 min-w-0">
          <div className="flex justify-between items-center">
            <Skeleton className="h-6 w-1/4" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-20 rounded-md" />
              <Skeleton className="h-8 w-20 rounded-md" />
            </div>
          </div>
          <Skeleton className="h-64 w-full rounded-lg" />
          <div className="flex justify-between items-center mt-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        {/* Recent activity and reports */}
        <div className="w-full lg:w-[320px] flex-shrink-0 flex flex-col gap-4">
          <div className="bg-white rounded-xl p-6">
            <Skeleton className="h-6 w-1/2 mb-4" />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start gap-3 mb-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
          {/* Reports list skeleton */}
          <div className="bg-white rounded-xl p-6">
            <Skeleton className="h-6 w-1/3 mb-4" />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 mb-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-32 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  </div>
);

interface AppDashboardSkeletonProps {
  show?: boolean;
}

export default function AppDashboardSkeleton({
  show = true,
}: AppDashboardSkeletonProps) {
  const isAuth = window.location.pathname.includes('auth');

  const isSideBar = useAtomValue(sidebarAtom);

  if (!show) return null;

  if (isAuth) {
    return (
      <div className="min-h-svh w-full flex items-center justify-center bg-[#F8FAFC] p-4">
        <div className="w-full max-w-md space-y-6 animate-pulse">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src={LogoWithText}
              alt="ViewMetrics's logo with text"
              className="h-8"
            />
          </div>

          {/* Welcome back text */}
          <div className="text-center space-y-2">
            <Skeleton className="h-8 w-48 mx-auto rounded-md bg-gray-300/30" />
            <Skeleton className="h-4 w-64 mx-auto rounded-md bg-gray-300/30" />
          </div>

          {/* Google login button */}
          <Skeleton className="h-12 w-full rounded-md bg-gray-300/30" />

          {/* Divider */}
          <div className="relative">
            <Skeleton className="h-4 w-full rounded-md bg-gray-300/30" />
          </div>

          {/* Email input */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-24 rounded-md bg-gray-300/30" />
            <Skeleton className="h-12 w-full rounded-md bg-gray-300/30" />
          </div>

          {/* Password input */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-24 rounded-md bg-gray-300/30" />
            <Skeleton className="h-12 w-full rounded-md bg-gray-300/30" />
          </div>

          {/* Login button */}
          <Skeleton className="h-12 w-full rounded-md bg-gray-300/30" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-svh w-svw font-sans flex max-w-screen-2xl mx-auto border-x">
      {/* Sidebar */}
      {isSideBar && (
        <aside className="hidden md:flex flex-col bg-sidebar w-[280px] border-r border-grayscale-100 px-4 gap-4 animate-pulse relative z-10">
          <div className="flex items-center justify-between h-[72px]">
            <img
              src={LogoWithText}
              alt="ViewMetrics's logo with text"
              className="h-8"
            />
          </div>
          <div className="mb-1">
            <Skeleton className="h-10 w-full rounded-md mb-2 bg-gray-300/30" />
            <Skeleton className="h-6 w-3/4 rounded-md bg-gray-300/30" />
          </div>
          <nav className="flex flex-col gap-2 flex-1">
            {/* Main navigation items */}
            <div className="space-y-1">
              {/* Dashboard */}
              <NavigationSkeleton showIcon />

              {/* Analytics Section */}
              <NavigationSkeleton showIcon />
              <div className="space-y-1">
                <NavigationSkeleton isNested showIcon />
              </div>

              {/* Content Section */}
              <NavigationSkeleton showIcon />
              <div className="space-y-1">
                <NavigationSkeleton isNested showIcon />
              </div>

              {/* Settings Section */}
              <NavigationSkeleton showIcon />
              <div className="space-y-1">
                <NavigationSkeleton isNested showIcon />
                <NavigationSkeleton isNested showIcon />
              </div>
            </div>
          </nav>
          {/* Pricing Card */}
          <div className="mt-auto bg-white/5 rounded-lg p-4 space-y-3">
            <Skeleton className="h-4 w-3/4 bg-gray-300/30" />
            <Skeleton className="h-6 w-1/2 bg-gray-300/30" />
            <Skeleton className="h-8 w-full rounded-md bg-gray-300/30" />
            <Skeleton className="h-3 w-full bg-gray-300/30" />
          </div>
        </aside>
      )}

      {/* if the side bar is not open i need to collplapse the sidebar */}
      {!isSideBar && (
        <aside className="hidden md:flex flex-col bg-sidebar w-[80px] border-r border-grayscale-100 px-4 gap-4 animate-pulse relative z-10 pt-6">
          <div className="flex items-center justify-between gap-4 flex-col">
            {[...Array(4)].map((_, i) => (
              <Skeleton
                key={i}
                className="h-10 w-10 rounded-md bg-gray-300/30"
              />
            ))}
            {[...Array(4)].map((_, i) => (
              <Skeleton
                key={i}
                className="h-10 w-10 rounded-md bg-gray-300/30 rounded-full"
              />
            ))}
          </div>
        </aside>
      )}

      {/* Main content area */}
      <AppDashboardContentAreaSkeleton />
    </div>
  );
}
